import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { StockService } from "./stock.service";

const initialState = {
  stocks: [],
  pagination: null, // page, limit, total, pages
  summary: null,    // full-set counts: total, inStock, lowStock, outOfStock
  stock: null,
  history: [],
  historyPagination: null,
  historyLoading: false,
  success: false,
  isLoading: false,
  error: null,
  message: null,
};

export const addStock = createAsyncThunk(
  "stock/addStock",
  async (data, { rejectWithValue, getState }) => {
    try {
      const _stockService = new StockService();
      const response = await _stockService.addStock(data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getStocks = createAsyncThunk(
  "stock/getStocks",
  async (params, { rejectWithValue, getState }) => {
    try {
      const _stockService = new StockService();
      const response = await _stockService.getStocks(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getStock = createAsyncThunk(
  "stock/getStock",
  async (id, { rejectWithValue, getState }) => {
    try {
      const _stockService = new StockService();
      const response = await _stockService.getStock(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateStock = createAsyncThunk(
  "stock/updateStock",
  async (data, { rejectWithValue, getState }) => {
    try {
      const _stockService = new StockService();
      const response = await _stockService.updateStock(data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteStock = createAsyncThunk(
  "stock/deleteStock",
  async (id, { rejectWithValue, getState }) => {
    try {
      const _stockService = new StockService();
      const response = await _stockService.deleteStock(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getStockHistory = createAsyncThunk(
  "stock/getStockHistory",
  async ({ id, page, limit }, { rejectWithValue }) => {
    try {
      const _stockService = new StockService();
      const response = await _stockService.getStockHistory(id, page, limit);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

const stockSlice = createSlice({
  name: "stock",
  initialState,
  reducers: {
    reset: (state) => {
      state.success = false;
      state.isLoading = false;
      state.error = null;
      state.message = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(addStock.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(addStock.fulfilled, (state, action) => {
      state.isLoading = false;
      state.success = true;
      state.stocks.push(action.payload);
    });
    builder.addCase(addStock.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });
    builder.addCase(getStocks.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getStocks.fulfilled, (state, action) => {
      state.isLoading = false;
      state.stocks = action.payload.data;
      state.pagination = action.payload.pagination;
      state.summary = action.payload.summary || null;
    });
    builder.addCase(getStocks.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });
    builder.addCase(getStock.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getStock.fulfilled, (state, action) => {
      state.isLoading = false;
      state.stock = action.payload.data;
    });
    builder.addCase(getStock.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    builder.addCase(updateStock.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(updateStock.fulfilled, (state, action) => {
      state.isLoading = false;
      state.success = true;
    });
    builder.addCase(updateStock.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    builder.addCase(deleteStock.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(deleteStock.fulfilled, (state, action) => {
      state.isLoading = false;
      state.success = true;
    });
    builder.addCase(deleteStock.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    builder.addCase(getStockHistory.pending, (state) => {
      state.historyLoading = true;
    });
    builder.addCase(getStockHistory.fulfilled, (state, action) => {
      state.historyLoading = false;
      state.history = action.payload.data;
      state.historyPagination = action.payload.pagination;
    });
    builder.addCase(getStockHistory.rejected, (state, action) => {
      state.historyLoading = false;
      state.error = action.payload;
    });
  },
});

export const { reset } = stockSlice.actions;

export default stockSlice.reducer;
